import { fetchConversationPermaclosedConfig } from 'api/conversation'
import store from 'config/store'
import { PRERENDERED_CONVO_ID } from 'constants/conversations'
import { triggerConversationFirstInteractionAPIEvent } from 'modules/api/events'
import { ActiveCampaign } from 'modules/campaigns/types'
import { sendMessage } from 'modules/message/sender'
import { omit, pathOr } from 'ramda'
import { filter, ignoreElements, tap } from 'rxjs/operators'
import { ConversationId } from 'types/conversation'
import { Message } from 'types/message'
import { ofType } from 'utils/redux'

import { permacloseConversation, receiveMessageSendFailure,receiveMessageSent } from './actions'
import { conversationHasEndUserMessage, isPrerenderedConversation } from './utils'

export const sendMessageEpic = (action$, state$) => action$.pipe(
  ofType("SEND_MESSAGE"),
  filter(() => store.name === 'controller'),
  tap(async ({ payload }) => {
    const activeCampaign: Partial<ActiveCampaign> = pathOr<Partial<ActiveCampaign>>({}, ['activeCampaign'], state$.value.campaigns)
    const activeConversationId: ConversationId = state$.value.conversations.activeConversation
    const isFirstMessageInPrerenderedCampaign = isPrerenderedConversation(activeConversationId)
    const isMessageStartingNewConversation = !payload.message.conversationId || payload.message.conversationId === PRERENDERED_CONVO_ID

    const getConversationId = () => {
      const isLiveViewConversation = pathOr(false, ['attributes', 'liveViewInitiated'], activeCampaign)

      if (isLiveViewConversation) {
        const campaignConversationId = pathOr<number>(null, ['attributes', 'conversationId'], activeCampaign)
        return campaignConversationId
      }

      if (isFirstMessageInPrerenderedCampaign || !activeConversationId) {
        return undefined
      }

      return activeConversationId
    }

    let messageWithDefaultAttributes: Message = {
      ...payload.message,
      conversationId: getConversationId(),
      attributes: {
        ...(payload.message.attributes ? payload.message.attributes : {}),
        ...isFirstMessageInPrerenderedCampaign ? state$.value.conversations.resumeFlowContext : {},
        embedId: state$.value.embed.id
      }
    }

    // don't send up local failed status
    messageWithDefaultAttributes = omit(['failedToSend'],messageWithDefaultAttributes)

    const sendMessageResult  = await sendMessage(messageWithDefaultAttributes)
    if (!sendMessageResult) {
      store.dispatch(receiveMessageSendFailure({
        message:payload.message,
        isPrerendered: isFirstMessageInPrerenderedCampaign,
        isNew: isMessageStartingNewConversation
      }))
      return
    }

    const { data: messageData } = sendMessageResult

    store.dispatch(receiveMessageSent({
      message: messageData,
      isPreRendered: isFirstMessageInPrerenderedCampaign,
      isNew: isMessageStartingNewConversation
    }))
  }),
  ignoreElements() //this is so important. Removing will cause an infinite loop of msg sends
)

export const receiveMessageSentEpic = (action$, state$) => action$.pipe(
  ofType('RECEIVE_MESSAGE_SENT'),
  filter(() => store.name === 'controller'),
  tap(({ payload: { message } }: { payload: { message: Message } }) => {
    if (!conversationHasEndUserMessage(message)) {
      triggerConversationFirstInteractionAPIEvent(message)
    }
  }),
  ignoreElements()
)

export const checkPermacloseConversationEpic = (action$, state$) => action$.pipe(
  ofType('SET_ACTIVE_CONVERSATION'),
  filter(() => store.name === 'controller'),
  tap(async ({ payload: { conversationId } }: { payload: { conversationId: ConversationId } }) => {
    if (conversationId < 0 || conversationId === PRERENDERED_CONVO_ID) {
      return
    }

    const isPermaclosed = pathOr<boolean>(false, ['conversations', 'conversationStatus', conversationId, 'permaclosed'], state$.value)
    if (isPermaclosed) {
      return
    }

    const numberConversationId = conversationId as number

    const { data: { sentMessage = false } } = await fetchConversationPermaclosedConfig(numberConversationId)
    if (sentMessage) {
      store.dispatch(permacloseConversation({ conversationId: numberConversationId }))
    }
  }),
  ignoreElements()
)
